import { NarratoClient } from './client';
import type { EventProperties, NarratoConfig } from './types';

export { NarratoClient } from './client';
export { SDK_VERSION } from './version';
export type { AnalyticsEvent, EventProperties, NarratoConfig } from './types';

let client: NarratoClient | null = null;

/**
 * Initialize the shared Narrato client. Call once, as early as possible.
 *
 * ```ts
 * import * as narrato from '@narrato-app/sdk-web';
 * narrato.init('<write-key>', { host: 'http://localhost:8000' });
 * narrato.track('signup_completed', { plan: 'free' });
 * ```
 */
export function init(
  writeKey: string,
  options: Omit<NarratoConfig, 'writeKey'> = {},
): NarratoClient {
  if (client) {
    console.warn('[narrato] init() called more than once; ignoring');
    return client;
  }
  client = new NarratoClient({ writeKey, ...options });
  return client;
}

/** Queue a custom event. No-op (with a console warning) before init(). */
export function track(eventName: string, properties?: EventProperties): void {
  requireClient()?.track(eventName, properties);
}

/** Associate subsequent events with a known user. */
export function identify(userId: string, traits?: EventProperties): void {
  requireClient()?.identify(userId, traits);
}

/** Track a page view (URL, title, referrer captured automatically). */
export function page(properties?: EventProperties): void {
  requireClient()?.page(properties);
}

/** Clear identity on logout: new anonymous id, new session. */
export function reset(): void {
  requireClient()?.reset();
}

/** Send every queued event now. */
export async function flush(): Promise<void> {
  await requireClient()?.flush();
}

/** Tear down the shared client (tests / HMR). */
export function shutdown(): void {
  client?.shutdown();
  client = null;
}

function requireClient(): NarratoClient | null {
  if (!client) {
    console.warn('[narrato] SDK not initialized — call init(writeKey) first');
  }
  return client;
}
